<script setup lang="ts">
import { Clock, ClipboardCheck, BookOpen, Users } from 'lucide-vue-next'
import { AppButton, EntityIcon, type PaletteHue } from '@/design-system/components'

/**
 * NextLessonHero (§03) — the prominent next-lesson card, themed to its class
 * hue: radial wash from the top-right + a 4px solid bar down the left edge.
 */
defineProps<{
  hue: PaletteHue
  className: string
  startsLabel: string
  timeRange: string
  room?: string | null
  title?: string | null
  topic?: string | null
}>()
const emit = defineEmits<{ takeRegister: []; openPlan: []; openClass: [] }>()
</script>

<template>
  <div class="hero">
    <div
      class="hero__wash"
      :style="{ background: `radial-gradient(120% 130% at 100% 0%, ${hue.soft} 0%, rgba(255,255,255,0) 58%)` }"
    />
    <div class="hero__bar" :style="{ background: hue.solid }" />
    <div class="hero__body">
      <div class="hero__eyebrow">
        <span class="hero__clock" :style="{ background: hue.soft, color: hue.ink }">
          <Clock :size="13" />
        </span>
        <span class="hero__eyebrow-text" :style="{ color: hue.ink }">
          Next lesson · {{ startsLabel }}
        </span>
      </div>

      <div class="hero__main">
        <EntityIcon :icon="Users" :palette="hue" :size="54" :corner-radius="15" />
        <div class="hero__detail">
          <div class="hero__titlerow">
            <h2 class="hero__class">{{ className }}</h2>
            <span class="hero__time">{{ timeRange }}</span>
            <span v-if="room" class="hero__room">· {{ room }}</span>
          </div>
          <div v-if="title" class="hero__lesson">{{ title }}</div>
          <div v-if="topic" class="hero__topic">{{ topic }}</div>
          <div class="hero__actions">
            <AppButton variant="primary" size="md" @click="emit('takeRegister')">
              <ClipboardCheck :size="16" /> Take register
            </AppButton>
            <AppButton variant="secondary" size="md" @click="emit('openPlan')">
              <BookOpen :size="16" /> Open plan
            </AppButton>
            <AppButton variant="ghost" size="md" @click="emit('openClass')">Open class</AppButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hero {
  position: relative;
  border-radius: 16px;
  background: var(--surface);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  font-family: var(--font-sans);
}
.hero__wash {
  position: absolute;
  inset: 0;
}
.hero__bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}
.hero__body {
  position: relative;
  padding: 22px 24px;
}
.hero__eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.hero__clock {
  display: inline-flex;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  align-items: center;
  justify-content: center;
}
.hero__eyebrow-text {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}
.hero__main {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}
.hero__detail {
  flex: 1;
  min-width: 0;
}
.hero__titlerow {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.hero__class {
  margin: 0;
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.5px;
  color: var(--text-primary);
}
.hero__time {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 15px;
  font-weight: 500;
  color: var(--ink-600);
}
.hero__room {
  font-size: 14px;
  color: var(--ink-500);
}
.hero__lesson {
  margin-top: 7px;
  font-size: 16px;
  font-weight: 500;
  color: var(--ink-800);
}
.hero__topic {
  margin-top: 3px;
  font-size: 14px;
  color: var(--ink-600);
}
.hero__actions {
  display: flex;
  gap: 9px;
  margin-top: 18px;
  flex-wrap: wrap;
}
</style>
